import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, Eye, Copy, Library, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const TYPES = ['all', 'video', 'image', 'audio', 'caption', 'hashtags', 'blog_outline', 'ad_copy', 'short_script', 'subtitle'];
const TYPE_LABELS = { all: 'Tümü', video: 'Video', caption: 'Başlık', hashtags: 'Hashtag', blog_outline: 'Blog', ad_copy: 'Reklam', short_script: 'Senaryo', image: 'Görsel', audio: 'Ses', subtitle: 'Altyazı' };

export default function ContentLibraryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const streamUrl = (url) => url ? `${backendUrl}${url.replace('/api/media/', '/api/media/stream/')}` : '';

  const load = (type, search) => {
    let params = [];
    if (type && type !== 'all') params.push(`content_type=${type}`);
    if (search) params.push(`search=${encodeURIComponent(search)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    api.get(`/content/library${qs}`)
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(filter, searchQuery); }, [filter]);

  const handleSearch = () => { load(filter, searchQuery); };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/content/library/${id}`);
      toast.success('İçerik silindi');
      load(filter, searchQuery);
    } catch { toast.error('Silme başarısız'); }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content || '');
    toast.success('Kopyalandı');
  };

  const renderPreview = (item) => {
    if (item.type === 'image' && item.url) {
      return <img src={`${backendUrl}${item.url}`} alt={item.title} className="w-full h-32 object-cover rounded-md border border-[#262626] mb-2" />;
    }
    if (item.type === 'audio' && item.url) {
      return <audio controls className="w-full mb-2 h-8" src={streamUrl(item.url)} />;
    }
    if (item.type === 'video' && item.url) {
      return <video controls className="w-full h-32 object-cover rounded-md border border-[#262626] mb-2" preload="metadata" src={streamUrl(item.url)} />;
    }
    if (item.type === 'subtitle' && item.content) {
      return <pre className="text-xs text-[#737373] font-jetbrains line-clamp-3 mb-2 bg-[#0A0A0A] p-2 rounded">{item.content.slice(0, 120)}</pre>;
    }
    return <p className="text-xs text-[#737373] font-ibm line-clamp-3 mb-2">{item.content?.slice(0, 150)}</p>;
  };

  const renderDetailView = (item) => {
    if (!item) return null;
    if (item.type === 'video' && item.url) {
      return (
        <div className="space-y-3">
          <video controls className="w-full rounded-lg border border-[#262626]" src={streamUrl(item.url)} data-testid="library-video-player" />
          <div className="flex items-center gap-3">
            <a href={`${backendUrl}${item.url}`} download className="inline-flex items-center gap-1 text-sm text-[#FACC15] hover:text-white font-ibm"><Download size={14} /> İndir</a>
            {item.metadata?.duration && <span className="text-xs text-[#525252] font-jetbrains">{Math.round(item.metadata.duration)}s</span>}
          </div>
          {item.metadata?.poster && <img src={`${backendUrl}${item.metadata.poster}`} alt="Poster" className="w-32 rounded border border-[#262626]" />}
          {item.content && <pre className="whitespace-pre-wrap text-xs text-[#A3A3A3] font-ibm bg-[#0A0A0A] p-3 rounded border border-[#1A1A1A] max-h-[150px] overflow-y-auto">{item.content}</pre>}
        </div>
      );
    }
    if (item.type === 'image' && item.url) {
      return (
        <div className="space-y-3">
          <img src={`${backendUrl}${item.url}`} alt={item.title} className="w-full rounded-lg border border-[#262626]" />
          <a href={`${backendUrl}${item.url}`} download className="inline-flex items-center gap-1 text-sm text-[#FACC15] hover:text-white font-ibm"><Download size={14} /> İndir</a>
        </div>
      );
    }
    if (item.type === 'audio' && item.url) {
      return (
        <div className="space-y-3">
          <audio controls className="w-full" src={streamUrl(item.url)} />
          <a href={`${backendUrl}${item.url}`} download className="inline-flex items-center gap-1 text-sm text-[#FACC15] hover:text-white font-ibm"><Download size={14} /> İndir</a>
          {item.metadata?.voice && <p className="text-xs text-[#525252] font-jetbrains">Ses: {item.metadata.voice} | Model: {item.metadata.model || ''}</p>}
        </div>
      );
    }
    return <pre className="whitespace-pre-wrap text-sm text-[#F5F5F5] font-ibm leading-relaxed">{item.content}</pre>;
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Kütüphane yükleniyor...</div>;

  return (
    <div data-testid="content-library-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Medya Kütüphanesi</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">{total} kayıtlı varlık</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Başlıkta ara..."
            className="bg-[#141414] border-[#262626] text-white w-64 h-9"
            data-testid="library-search-input"
          />
          <Button variant="outline" onClick={handleSearch} className="border-[#262626] text-[#A3A3A3] hover:text-white h-9 px-3" data-testid="library-search-btn">
            <Search size={16} />
          </Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-[#141414] border border-[#262626] flex-wrap">
          {TYPES.map((t) => (
            <TabsTrigger
              key={t} value={t} data-testid={`filter-${t}`}
              className="capitalize data-[state=active]:bg-white data-[state=active]:text-black font-ibm text-xs"
            >
              {TYPE_LABELS[t] || t.replace('_', ' ')}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <Library size={40} className="mx-auto text-[#262626] mb-4" />
          <p className="text-[#737373] font-ibm text-sm">Henüz içerik kaydedilmedi</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="bg-[#141414] border-[#262626] hover:border-[#404040] transition-colors duration-200" data-testid={`content-card-${item.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 capitalize text-xs">{TYPE_LABELS[item.type] || item.type?.replace('_', ' ')}</Badge>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(item)} className="text-[#A3A3A3] hover:text-white h-7 w-7 p-0" data-testid={`view-${item.id}`}><Eye size={14} /></Button>
                    {item.content && <Button variant="ghost" size="sm" onClick={() => handleCopy(item.content)} className="text-[#A3A3A3] hover:text-white h-7 w-7 p-0"><Copy size={14} /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-[#A3A3A3] hover:text-red-400 h-7 w-7 p-0" data-testid={`delete-${item.id}`}><Trash2 size={14} /></Button>
                  </div>
                </div>
                <h3 className="text-sm font-ibm text-white font-medium mb-2 line-clamp-1">{item.title}</h3>
                {renderPreview(item)}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-[#525252] font-jetbrains">{new Date(item.created_at).toLocaleDateString('tr-TR')}</p>
                  {item.metadata?.provider && <span className="text-xs text-[#525252] font-jetbrains">{item.metadata.provider}/{item.metadata.model}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-white">{selected?.title}</DialogTitle>
          </DialogHeader>
          {renderDetailView(selected)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
